/**
 * Gobodo Button Action Service
 *
 * CRUD for ButtonAction__c records.
 */

import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import GobodoConfig from '../../config/gobodoConfig';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { handleGobodoError, logGobodoError } from '../../utils/gobodoErrorHandler';
import { retryWithBackoff } from '../../utils/gobodoRetry';
import { ensureObjectTypeExists } from '../../utils/gobodoObjectTypeVerifier';
import { serializeGobodoData } from '../providers/gobodoRoleProvider';
import { objectNameToApiName } from './gobodoTabConfigService';
import type { ButtonAction, ActionConfig, VisibilityRule } from '../queries/buttonActionQueries';

const OBJECT_KEY = 'ButtonAction__c';

function toIsoString(val: any): string {
  if (val == null) return new Date().toISOString();
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && typeof val.toISOString === 'function') return val.toISOString();
  return new Date().toISOString();
}

function parseJson<T>(val: any, fallback: T): T {
  if (val == null) return fallback;
  if (typeof val === 'string' && val) {
    try {
      return JSON.parse(val) as T;
    } catch {
      return fallback;
    }
  }
  if (Array.isArray(val)) return val as T;
  if (typeof val === 'object') return val as T;
  return fallback;
}

function gobodoToButtonAction(record: any): ButtonAction {
  const raw = record.properties || record.data || {};
  const data = serializeGobodoData(raw) as Record<string, any>;
  const action = parseJson<ActionConfig>(data.actionJson, { type: 'call-api' });
  const visibilityRules = parseJson<VisibilityRule[]>(data.visibilityRulesJson, []);
  return {
    id: String(record.id),
    name: data.name || '',
    objectName: data.objectTypeKey || '',
    type: (data.type === 'quick-action' ? 'quick-action' : 'button') as 'button' | 'quick-action',
    location: (data.location || 'detail-header') as any,
    label: data.label || '',
    icon: data.icon || undefined,
    color: data.color || undefined,
    action,
    successMessage: data.successMessage || '',
    errorMessage: data.errorMessage || '',
    visibilityRules,
    visibilityLogic: (data.visibilityLogic === 'OR' ? 'OR' : 'AND') as 'AND' | 'OR' | undefined,
    isActive: Boolean(data.isActive ?? true),
    createdBy: String(record.creatorid || data.createdBy || 'system'),
    createdDate: toIsoString(record.creationdate ?? data.createdAt),
    lastModified: toIsoString(record.modificationdate ?? data.updatedAt),
    lastModifiedBy: String(data.updatedBy || record.creatorid || 'system'),
  };
}

function buttonActionToGobodoProps(btn: Partial<ButtonAction>): Record<string, any> {
  return {
    objectTypeKey: btn.objectName || '',
    name: btn.name || '',
    type: btn.type ?? 'button',
    location: btn.location ?? 'detail-header',
    label: btn.label ?? '',
    icon: btn.icon ?? '',
    color: btn.color ?? '',
    actionJson: btn.action ? JSON.stringify(btn.action) : '{}',
    visibilityRulesJson: btn.visibilityRules ? JSON.stringify(btn.visibilityRules) : '[]',
    visibilityLogic: btn.visibilityLogic ?? 'AND',
    successMessage: btn.successMessage ?? '',
    errorMessage: btn.errorMessage ?? '',
    isActive: btn.isActive ?? true,
    createdBy: btn.createdBy || 'system',
    updatedBy: btn.lastModifiedBy || btn.createdBy || 'system',
  };
}

export async function fetchButtonActions(objectTypeKey: string): Promise<ButtonAction[]> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const apiName = objectNameToApiName(objectTypeKey);
  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  const allRecords = await retryWithBackoff(
    () =>
      objectsApi.getObjectsInGraphByType(GobodoConfig.graph(), OBJECT_KEY, { page: 0, size: 100 }),
    { maxRetries: 2 }
  );

  const records = (allRecords || []).filter((r: any) => {
    const p = serializeGobodoData(r.properties || r.data || {}) as Record<string, any>;
    return (p.objectTypeKey || '') === apiName;
  });

  return records
    .map((r: any) => gobodoToButtonAction(r))
    .sort((a: ButtonAction, b: ButtonAction) =>
      new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
    );
}

export async function fetchButtonAction(id: string): Promise<ButtonAction> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  const record = await retryWithBackoff(() => objectsApi.getObjectById(parseInt(id)), {
    maxRetries: 3,
  });
  return gobodoToButtonAction(record);
}

export async function createButtonAction(
  data: Omit<ButtonAction, 'id' | 'createdBy' | 'createdDate' | 'lastModified' | 'lastModifiedBy'>
): Promise<ButtonAction> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  await ensureObjectTypeExists(OBJECT_KEY, {
    label: 'Button Action',
    pluralLabel: 'Button Actions',
    description: 'Custom button and quick action configuration per object',
    icon: 'MousePointer',
    category: 'system',
  });

  const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);
  const objectType = await retryWithBackoff(() => objectTypesApi.getObjectTypeByKey(OBJECT_KEY, { maxFields: 0 }), {
    maxRetries: 3,
  });

  const apiName = objectNameToApiName(data.objectName);
  const props = buttonActionToGobodoProps({
    ...data,
    objectName: apiName,
    createdBy: 'Current User',
    lastModifiedBy: 'Current User',
  });

  const payload = new GobodoApi.GraphObjectPayload();
  payload.typekey = OBJECT_KEY;
  payload.typeid = objectType.id as any;
  payload.label = `${apiName} - ${data.name}`;
  payload.graphs = [GobodoConfig.graph()];
  payload.properties = props;

  const created = await retryWithBackoff(() => objectsApi.createObject(payload), { maxRetries: 3 });
  return gobodoToButtonAction(created);
}

export async function updateButtonAction(
  id: string,
  updates: Partial<Pick<ButtonAction, 'name' | 'type' | 'location' | 'label' | 'icon' | 'color' | 'action' | 'visibilityRules' | 'visibilityLogic' | 'successMessage' | 'errorMessage' | 'isActive'>>
): Promise<ButtonAction> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  const existing = await retryWithBackoff(() => objectsApi.getObjectById(parseInt(id)), {
    maxRetries: 3,
  });
  const current = gobodoToButtonAction(existing);

  const merged: ButtonAction = { ...current, ...updates };
  const props = buttonActionToGobodoProps(merged);

  const payload = new GobodoApi.GraphObjectPayload();
  payload.typekey = OBJECT_KEY;
  payload.typeid = (existing as any).typeid;
  payload.label = existing.label;
  payload.graphs = [GobodoConfig.graph()];
  payload.properties = { ...(existing.properties || existing.data || {}), ...props };

  const updated = await retryWithBackoff(() => objectsApi.updateObject(parseInt(id), payload), {
    maxRetries: 3,
  });
  return gobodoToButtonAction(updated);
}

export async function deleteButtonAction(id: string): Promise<void> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  try {
    await retryWithBackoff(() => objectsApi.deleteObject(parseInt(id)), { maxRetries: 3 });
  } catch (error: any) {
    const gobodoError = handleGobodoError(error, { operation: 'deleteButtonAction', objectId: id });
    if (gobodoError) {
      logGobodoError(gobodoError);
      throw gobodoError;
    }
    throw error;
  }
}
